import { useEffect, useMemo, useState } from "react";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import { SortableTableHead } from "../../common/SortableTableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { usePatrolsStore } from "./patrolsStore/patrolsStore";
import { formatPatrolTime } from "./patrolsUtils/formatPatrolTimestamp";
import { useNavigate } from "react-router-dom";
import { Order } from "../Alerts/AlertsUtils/alertsInterfaces";
import {
  getComparator,
  stableSort,
} from "../Alerts/AlertsUtils/alertsTableFunctions";

const ALERTS_HEAD_CELLS = [
  { id: "camera_name", label: "Camera Name" },
  { id: "timestamp", label: "Time" },
  { id: "detection_code", label: "Alert Type" },
  { id: "schedule_name", label: "Schedule" },
] as const;

export const AlertsTableSelectedPatrol = () => {
  const navigate = useNavigate();
  const {
    patrolAlerts,
    selectedPatrolAlertIndex,
    setSelectedPatrolAlertIndex,
    fetchPatrolAlertVideo,
  } = usePatrolsStore();

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("timestamp");

  const sortedAlerts = useMemo(
    () => stableSort(patrolAlerts, getComparator(order, orderBy)),
    [patrolAlerts, order, orderBy]
  );

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Auto-select first row and fetch video when alerts data changes (use first row of sorted list)
  useEffect(() => {
    const sorted = stableSort(
      patrolAlerts,
      getComparator(order, orderBy)
    );
    const firstWindowId = sorted[0]?.window_id;
    if (sorted.length > 0 && firstWindowId) {
      setSelectedPatrolAlertIndex(0);
      fetchPatrolAlertVideo(String(firstWindowId), navigate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset selection when alerts data changes, not when sort order changes
  }, [
    patrolAlerts,
    setSelectedPatrolAlertIndex,
    fetchPatrolAlertVideo,
    navigate,
  ]);

  const handleRowClick = (index: number) => {
    setSelectedPatrolAlertIndex(index);
    const windowId = sortedAlerts[index]?.window_id;
    if (windowId) {
      fetchPatrolAlertVideo(String(windowId), navigate);
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        height: 450,
        width: "100%",
        maxWidth: 800,
        minWidth: 0,
        backgroundColor: "#f8fafd",
        overflow: "auto",
      }}
    >
      <Table stickyHeader>
        <SortableTableHead
          headCells={[...ALERTS_HEAD_CELLS]}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          backgroundColor="#e3ecfa"
        />
        <TableBody>
          {sortedAlerts.length > 0 ? (
            sortedAlerts.map((alert, index) => (
              <TableRow
                key={`${alert.camera_id}-${index}`}
                onClick={() => handleRowClick(index)}
                sx={{
                  backgroundColor:
                    selectedPatrolAlertIndex === index ? "#d4e4f7" : "inherit",
                  "&:hover": {
                    backgroundColor:
                      selectedPatrolAlertIndex === index
                        ? "#c4d8f0"
                        : "#eaf1fb",
                    cursor: "pointer",
                  },
                }}
              >
                <TableCell sx={{ width: "35%" }}>
                  {alert.site_id && alert.camera_id && alert.camera_name ? (
                    <a
                      href={`sites/${alert.site_id}/editcamera/${alert.camera_id}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: "#0d47a1",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#002171";
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#0d47a1";
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      {alert.camera_name}
                    </a>
                  ) : (
                    alert.camera_name || "N/A"
                  )}
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  {formatPatrolTime(
                    typeof alert.timestamp === "string"
                      ? alert.timestamp
                      : String(alert.timestamp)
                  )}
                </TableCell>
                <TableCell sx={{ width: "25%" }}>
                  {alert.detection_code}
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  {alert.schedule_name ?? "—"}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No alerts for this patrol
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
